import {
  toWSDateString,
  WSActionsButton,
  WSAlert,
  WSAvatar,
  WSButton,
  WSCard,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSInfoBox,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import type { planEnrollmentApiSpec } from "@wingspanhq/wingspan-api-types";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { WSQueries } from "../../../query/WSQuery";
import { useQueryPlanEnrollments } from "../../../query/benefits/useQueryPlanEnrollments";
import { useMemberRegionIsAllowed } from "../../../shared/hooks/useGetMemberRegionIsAllowed";
import { useGetNow } from "../../../shared/hooks/useGetNow";
import { useModalStopCancellation } from "../../MyResources/components/useModalStopCancellation";
import {
  getPathnameDetailsFromEnrollment,
  getPathnameMyResourcesSubmitCancellation
} from "../../MyResources/paths";
import {
  getBenefitsEnrollInsuranceFlowPathname,
  getBenefitsTabPathname
} from "../paths";
import { selectorAvailablePlanEnrollments } from "../selectors/selectorAvailablePlanEnrollments";
import { BenefitInsuranceTemplate } from "../types";

type IPlanType = planEnrollmentApiSpec.PlanEnrollment["planType"];

interface BenefitsInsurancePageProps {
  insurance: BenefitInsuranceTemplate;
  parentPath?: string;
  planType: IPlanType;
}

export const BenefitsInsurancePage: React.FC<BenefitsInsurancePageProps> = ({
  insurance,
  parentPath,
  planType
}) => {
  const history = useHistory();

  const modalStopCancellation = useModalStopCancellation();

  useBrowserPageTitle(`Benefits - ${insurance.title}`);

  const now = useGetNow();

  const qPlanEnrollments = useQueryPlanEnrollments({
    filter: {
      planType: planType
    },
    sort: "createdAt"
  });

  const memberRegionIsAllowed = useMemberRegionIsAllowed(
    insurance.regionConfig
  );

  return (
    <WSQueries
      queries={{
        qPlanEnrollments
      }}
    >
      {({ qPlanEnrollmentsData }) => {
        const { title, about, costDescription, slug } = insurance;

        const planEnrollment = selectorAvailablePlanEnrollments(
          qPlanEnrollmentsData,
          now
        ).find(Boolean);

        const isSuccessfullyEnrolled = planEnrollment?.status === "Active";
        const isPending = planEnrollment?.status === "Pending";
        const isTerminated = planEnrollment?.status === "Terminated";
        const isArchived =
          isTerminated &&
          planEnrollment?.endDate &&
          planEnrollment?.endDate < now;

        const showGettingStartedButton = !planEnrollment || isPending;
        const showStopCancellationButton = isTerminated && !isArchived;
        const showSubmitCancellationButton =
          isSuccessfullyEnrolled && !isTerminated && !isArchived;
        const showViewPolicyButton =
          isSuccessfullyEnrolled || isTerminated || isArchived;

        const viewDetailsButton = {
          label: "View policy details",
          onClick: () => {
            history.push(
              getPathnameDetailsFromEnrollment(planEnrollment!.planEnrollmentId)
            );
          },
          visible: showViewPolicyButton,
          buttonKind: "Primary"
        };

        const cancelEnrollmentButton = {
          label: "Submit cancellation",
          destructive: true,
          visible: showSubmitCancellationButton,
          onClick: () => {
            history.push(
              getPathnameMyResourcesSubmitCancellation(
                planEnrollment!.planEnrollmentId
              )
            );
          }
        };

        const getStartedButton = {
          label: "Get started",
          onClick: () => {
            history.push(getBenefitsEnrollInsuranceFlowPathname(slug), {
              backPath: history.location.pathname
            });
          },
          disabled: isPending || !memberRegionIsAllowed,
          visible: showGettingStartedButton,
          buttonKind: "Primary"
        };

        const stopCancellationButton = {
          visible: showStopCancellationButton,
          onClick() {
            modalStopCancellation.open({
              planEnrollmentId: planEnrollment?.planEnrollmentId!,
              planType: planEnrollment?.planType!
            });
          },
          label: "Stop cancellation and keep enrollment"
        };

        const cardButtons: WSActionsButton[] = [
          viewDetailsButton,
          getStartedButton
        ].map(b => ({
          ...b,
          fullWidth: true
        }));

        const headerActions = [
          viewDetailsButton,
          getStartedButton,
          stopCancellationButton,
          cancelEnrollmentButton
        ] as WSPageToolbarAction[];

        return (
          <WSPage colorBackground="gray50">
            <WSPageToolbar
              breadcrumbs={[
                {
                  label: "Benefits",
                  onClick() {
                    parentPath
                      ? history.push(parentPath)
                      : history.push(getBenefitsTabPathname("all"));
                  }
                },
                {
                  label: title
                }
              ]}
              title={title}
              actions={headerActions}
            />
            {isSuccessfullyEnrolled ? (
              <WSAlert
                title="You’re enrolled!"
                theme="neutral"
                mb="L"
                icon="check-circle"
              >
                Congrats! You’re enrolled in <b>{title}</b>. View the details of
                your policy in the “My resources” section of your settings.
                <WSElement mt="S">
                  <WSButton.Link
                    rightIcon="chevron-right"
                    onClick={viewDetailsButton.onClick}
                  >
                    View your policy details
                  </WSButton.Link>
                </WSElement>
              </WSAlert>
            ) : showStopCancellationButton ? (
              <WSAlert
                title={`Enrollment is ending soon`}
                theme="neutral"
                icon="calendar"
                action={{
                  ...stopCancellationButton,
                  text: stopCancellationButton.label
                }}
                mb="XL"
              >
                You’ve submitted a cancellation for this enrollment and it will
                end on {toWSDateString(planEnrollment.endDate)}. If you change
                your mind, click “Stop cancellation and keep enrollment” below.
              </WSAlert>
            ) : !memberRegionIsAllowed ? (
              <WSAlert
                title="Not available in your region"
                theme="warning"
                icon="alert"
                mb="XL"
              >
                This policy doesn't meet your region's legal requirements.
                You'll need different coverage to comply with local laws.
              </WSAlert>
            ) : null}

            <WSGrid data-testid="benefitContent">
              <WSGrid.Item span={{ s: "8" }}>
                <WSPanel>
                  <WSElement mb="XL">{about}</WSElement>
                </WSPanel>
              </WSGrid.Item>

              <WSGrid.Item span={{ s: "4" }}>
                <WSCard
                  actions={{
                    buttons: cardButtons
                  }}
                >
                  <WSList gap="XL">
                    <WSFlexBox.CenterY gap="M" wrap="nowrap">
                      <WSAvatar.Icon
                        icon="shield-check-fill"
                        colorBackground="garnet"
                      />
                      <WSText.Paragraph weight="medium">
                        {title}
                      </WSText.Paragraph>
                    </WSFlexBox.CenterY>

                    <WSInfoBox theme="neutral">{costDescription}</WSInfoBox>
                  </WSList>
                </WSCard>
              </WSGrid.Item>
            </WSGrid>
          </WSPage>
        );
      }}
    </WSQueries>
  );
};
